import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';
import {
  Typography, Card, Table, Tag, Button, Modal, Form, Input, InputNumber,
  Space, Popconfirm, message, Switch, Tabs, Tooltip, Progress, Divider,
  Alert, Select,
} from 'antd';
import {
  PlusOutlined, SyncOutlined, ThunderboltOutlined,
  CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import { ipv4Validator, uuidV4Rule, httpsUrlRule } from '../../utils/validators';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import { getBrandLabel, type Brand } from '../../utils/tokenManager';
import { useBrandOptions } from '../../hooks/useBrandOptions';

const { Title, Text } = Typography;

/* ───── Types ───── */

interface VlessAccount {
  id: number;
  accountName: string;
  cfEmail: string | null;
  cfAccountId: string | null;
  workerName: string;
  workerUrl: string;
  workerSubdomain: string | null;
  uuid: string;
  allowedBrands?: Brand[] | null;
  dailyRequestLimit: number;
  dailyRequestUsed: number;
  healthStatus: string;
  latencyMs: number | null;
  lastHealthCheck: string | null;
  isEnabled: boolean;
  disabledReason: string | null;
}

interface PreferredIp {
  id: number;
  ipAddress: string;
  port: number;
  region: string;
  latencyMs: number | null;
  isEnabled: boolean;
  lastTested: string | null;
  sortOrder: number;
}

interface UsageStats {
  totalAccounts: number;
  enabledAccounts: number;
  totalDailyQuota: number;
  usedDailyQuota: number;
  accounts: Array<{
    id: number;
    accountName: string;
    workerName: string;
    healthStatus: string;
    latencyMs: number | null;
    dailyRequestUsed: number;
    dailyRequestLimit: number;
    usagePercent: number;
  }>;
}


/* ───── Health Tag ───── */
function HealthTag({ status }: { status: string }) {
  const map: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
    healthy: { color: 'green', icon: <CheckCircleOutlined />, text: '正常' },
    degraded: { color: 'orange', icon: <QuestionCircleOutlined />, text: '異常' },
    down: { color: 'red', icon: <CloseCircleOutlined />, text: '離線' },
    unknown: { color: 'default', icon: <QuestionCircleOutlined />, text: '未知' },
  };
  const s = map[status] || map.unknown;
  return <Tag color={s.color} icon={s.icon}>{s.text}</Tag>;
}

/* ═══════════════════════════════════════ */
/*  Main Page                             */
/* ═══════════════════════════════════════ */

export const VlessPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const sharedInfraReadOnly = !canMutateSharedInfra();

  return (
    <div className="page-enter">
      <Card size="small" styles={{ body: { padding: '12px 24px' } }} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CloudOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>VLESS / 優選 IP</Title>
        </div>
      </Card>

      <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 12 }} />

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'accounts', label: 'CF 帳號', children: <AccountsTab sharedInfraReadOnly={sharedInfraReadOnly} /> },
        { key: 'ips', label: '優選 IP', children: <PreferredIPsTab sharedInfraReadOnly={sharedInfraReadOnly} /> },
        { key: 'dashboard', label: '狀態總覽', children: <DashboardTab /> },
      ]} />
    </div>
  );
};

/* ═══════════════════════════════════════ */
/*  Accounts Tab                          */
/* ═══════════════════════════════════════ */

type SharedInfraTabProps = {
  sharedInfraReadOnly: boolean;
};

function AccountsTab({ sharedInfraReadOnly }: SharedInfraTabProps) {
  const brandOptions = useBrandOptions();
  const [accounts, setAccounts] = useState<VlessAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [modalLoading, setModalLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/vless/accounts');
      const data = await res.json();
      if (data.code === 0) setAccounts(data.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const openAdd = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ allowedBrands: [] });
    setModalOpen(true);
  };
  const openEdit = (r: VlessAccount) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingId(r.id);
    form.setFieldsValue({ ...r, allowedBrands: r.allowedBrands ?? [] });
    setModalOpen(true);
  };

  const handleOk = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      const url = editingId ? `/api/admin/vless/accounts/${editingId}` : '/api/admin/vless/accounts';
      const method = editingId ? 'PUT' : 'POST';
      const res = await authFetch(url, { method, body: JSON.stringify(values) });
      const data = await res.json();
      if (data.code === 0) {
        message.success(editingId ? '更新成功' : '新增成功');
        setModalOpen(false);
        fetch_();
      } else {
        message.error(data.msg || '操作失敗');
      }
    } catch { /* validation */ } finally { setModalLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const res = await authFetch(`/api/admin/vless/accounts/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.code === 0) { message.success('已刪除'); fetch_(); } else { message.error(data.msg); }
  };

  const handleToggle = async (id: number, isEnabled: boolean) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const res = await authFetch(`/api/admin/vless/accounts/${id}/toggle`, {
      method: 'PATCH', body: JSON.stringify({ isEnabled }),
    });
    const data = await res.json();
    if (data.code === 0) { message.success(isEnabled ? '已啟用' : '已停用'); fetch_(); }
  };

  const handleHealthCheck = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const res = await authFetch(`/api/admin/vless/accounts/${id}/health-check`, {
      method: 'POST',
    });
    const data = await res.json();
    if (data.code === 0) {
      message.success(`檢查完成: ${data.data.status} (${data.data.latencyMs || '-'}ms)`);
      fetch_();
    }
  };

  const handleDeploy = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    message.loading({ content: '正在部署 Worker...', key: 'deploy' });
    const res = await authFetch(`/api/admin/vless/accounts/${id}/deploy`, {
      method: 'POST',
    });
    const data = await res.json();
    if (data.code === 0) {
      message.success({ content: 'Worker 部署成功', key: 'deploy' });
      fetch_();
    } else {
      message.error({ content: data.msg || '部署失敗', key: 'deploy' });
    }
  };

  const handleHealthCheckAll = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    message.loading({ content: '批量檢查中...', key: 'hc' });
    const res = await authFetch('/api/admin/vless/accounts/health-check-all', {
      method: 'POST',
    });
    const data = await res.json();
    if (data.code === 0) {
      message.success({ content: `檢查完成: ${data.data.length} 個帳號`, key: 'hc' });
      fetch_();
    }
  };

  const columns = [
    { title: '名稱', dataIndex: 'accountName', key: 'accountName', width: 160 },
    { title: 'Worker', dataIndex: 'workerName', key: 'workerName', width: 180 },
    {
      title: '可見品牌',
      dataIndex: 'allowedBrands',
      key: 'allowedBrands',
      width: 170,
      render: (brands: Brand[] | null | undefined) =>
        brands && brands.length > 0 ? (
          <Space size={4} wrap>
            {brands.map((brand) => (
              <Tag key={brand} color="blue">
                {getBrandLabel(brand)}
              </Tag>
            ))}
          </Space>
        ) : (
          <Tag>全部品牌</Tag>
        ),
    },
    {
      title: '狀態', key: 'health', width: 80,
      render: (_: any, r: VlessAccount) => <HealthTag status={r.healthStatus} />,
    },
    {
      title: '延遲', dataIndex: 'latencyMs', key: 'latencyMs', width: 80,
      render: (v: number | null) => v != null ? `${v}ms` : '-',
    },
    {
      title: '配額', key: 'quota', width: 150,
      render: (_: any, r: VlessAccount) => {
        const pct = r.dailyRequestLimit > 0 ? Math.round((r.dailyRequestUsed / r.dailyRequestLimit) * 100) : 0;
        return (
          <Tooltip title={`${r.dailyRequestUsed.toLocaleString()} / ${r.dailyRequestLimit.toLocaleString()}`}>
            <Progress percent={pct} size="small" status={pct > 80 ? 'exception' : 'normal'} />
          </Tooltip>
        );
      },
    },
    {
      title: '啟用', key: 'enabled', width: 80,
      render: (_: any, r: VlessAccount) => (
        <Switch
          checked={r.isEnabled}
          onChange={(v) => handleToggle(r.id, v)}
          disabled={sharedInfraReadOnly}
          size="small"
        />
      ),
    },
    {
      title: '操作', key: 'actions', width: 240,
      render: (_: any, r: VlessAccount) => (
        <Space size="small">
          <Button size="small" disabled={sharedInfraReadOnly} onClick={() => openEdit(r)}>編輯</Button>
          <Button size="small" disabled={sharedInfraReadOnly} onClick={() => handleHealthCheck(r.id)} icon={<SyncOutlined />}>檢查</Button>
          <Button size="small" type="primary" disabled={sharedInfraReadOnly} onClick={() => handleDeploy(r.id)} icon={<ThunderboltOutlined />}>部署</Button>
          <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger disabled={sharedInfraReadOnly}>刪除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Cloudflare 帳號"
      extra={
        <Space>
          <Button disabled={sharedInfraReadOnly} onClick={handleHealthCheckAll} icon={<SyncOutlined />}>全部檢查</Button>
          <Button type="primary" disabled={sharedInfraReadOnly} onClick={openAdd} icon={<PlusOutlined />}>新增帳號</Button>
        </Space>
      }
    >
      <Table dataSource={accounts} columns={columns} rowKey="id" loading={loading} pagination={false} scroll={{ x: 1280 }} />

      <Modal
        title={editingId ? '編輯帳號' : '新增帳號'}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={modalLoading}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="accountName" label="帳號名稱" rules={[{ required: true }]}>
            <Input placeholder="如 cf-free-01" />
          </Form.Item>
          <Divider>Cloudflare 資訊</Divider>
          <Form.Item name="cfEmail" label="Email"><Input /></Form.Item>
          <Form.Item name="cfAccountId" label="Account ID"><Input /></Form.Item>
          <Form.Item name="cfApiToken" label="API Token" tooltip="用於自動部署 Worker">
            <Input.Password placeholder="cfut_..." />
          </Form.Item>
          <Divider>Worker 配置</Divider>
          <Form.Item name="workerName" label="Worker 名稱" rules={[{ required: true }]}>
            <Input placeholder="kailian-vless-01" />
          </Form.Item>
          <Form.Item
            name="workerUrl"
            label="Worker URL"
            tooltip="優先填 production custom domain（如 https://api-01.swiftmesh.net）。workers.dev 子域名歷經改名後容易失效造成 1101 錯誤,custom domain 才是 production 對外的真實 URL。"
            rules={[{ required: true }, httpsUrlRule]}
          >
            <Input placeholder="https://api-01.swiftmesh.net" />
          </Form.Item>
          <Form.Item
            name="workerSubdomain"
            label="workers.dev 子域名"
            tooltip="僅供記錄部署的 workers.dev 帳號子域名(如 ok-relay),不參與 health check。"
          >
            <Input placeholder="ok-relay" />
          </Form.Item>
          <Form.Item name="uuid" label="VLESS UUID" rules={[{ required: true }, uuidV4Rule]}>
            <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          </Form.Item>
          <Form.Item
            name="allowedBrands"
            label="可見品牌"
            tooltip="不選表示所有品牌都可見。"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="全部品牌"
              options={brandOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </Form.Item>
          <Divider>配額</Divider>
          <Form.Item name="dailyRequestLimit" label="每日請求上限">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="100000" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

/* ═══════════════════════════════════════ */
/*  Preferred IPs Tab                     */
/* ═══════════════════════════════════════ */

function PreferredIPsTab({ sharedInfraReadOnly }: SharedInfraTabProps) {
  const [ips, setIPs] = useState<PreferredIp[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [modalLoading, setModalLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/vless/ips');
      const data = await res.json();
      if (data.code === 0) setIPs(data.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const openAdd = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };
  const openEdit = (r: PreferredIp) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditingId(r.id);
    form.setFieldsValue(r);
    setModalOpen(true);
  };

  const handleOk = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      const url = editingId ? `/api/admin/vless/ips/${editingId}` : '/api/admin/vless/ips';
      const method = editingId ? 'PUT' : 'POST';
      const res = await authFetch(url, { method, body: JSON.stringify(values) });
      const data = await res.json();
      if (data.code === 0) {
        message.success(editingId ? '更新成功' : '新增成功');
        setModalOpen(false);
        fetch_();
      } else {
        message.error(data.msg || '操作失敗');
      }
    } catch { /* validation */ } finally { setModalLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const res = await authFetch(`/api/admin/vless/ips/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.code === 0) { message.success('已刪除'); fetch_(); }
  };

  const handleTest = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    message.loading({ content: '測速中...', key: `test-${id}` });
    const res = await authFetch(`/api/admin/vless/ips/${id}/test`, { method: 'POST' });
    const data = await res.json();
    if (data.code === 0) {
      const r = data.data;
      message.success({ content: r.latencyMs ? `${r.latencyMs}ms` : r.error, key: `test-${id}` });
      fetch_();
    }
  };

  const handleTestAll = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    message.loading({ content: '批量測速中...', key: 'test-all' });
    const res = await authFetch('/api/admin/vless/ips/test-all', { method: 'POST' });
    const data = await res.json();
    if (data.code === 0) {
      const available = data.data.filter((r: any) => r.latencyMs != null).length;
      message.success({ content: `測速完成: ${available}/${data.data.length} 可用`, key: 'test-all' });
      fetch_();
    }
  };

  const columns = [
    { title: 'IP', dataIndex: 'ipAddress', key: 'ipAddress', width: 160 },
    { title: '端口', dataIndex: 'port', key: 'port', width: 80 },
    { title: '地區', dataIndex: 'region', key: 'region', width: 100 },
    {
      title: '延遲', dataIndex: 'latencyMs', key: 'latencyMs', width: 100,
      render: (v: number | null) => {
        if (v == null) return <Tag>未測</Tag>;
        const color = v < 100 ? 'green' : v < 200 ? 'orange' : 'red';
        return <Tag color={color}>{v}ms</Tag>;
      },
      sorter: (a: PreferredIp, b: PreferredIp) => (a.latencyMs ?? 9999) - (b.latencyMs ?? 9999),
    },
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', width: 60 },
    {
      title: '啟用', key: 'enabled', width: 60,
      render: (_: any, r: PreferredIp) => r.isEnabled ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
    },
    {
      title: '操作', key: 'actions', width: 200,
      render: (_: any, r: PreferredIp) => (
        <Space size="small">
          <Button size="small" disabled={sharedInfraReadOnly} onClick={() => openEdit(r)}>編輯</Button>
          <Button size="small" disabled={sharedInfraReadOnly} onClick={() => handleTest(r.id)} icon={<ThunderboltOutlined />}>測速</Button>
          <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger disabled={sharedInfraReadOnly}>刪除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="優選 IP 池"
      extra={
        <Space>
          <Button disabled={sharedInfraReadOnly} onClick={handleTestAll} icon={<SyncOutlined />}>全部測速</Button>
          <Button type="primary" disabled={sharedInfraReadOnly} onClick={openAdd} icon={<PlusOutlined />}>新增 IP</Button>
        </Space>
      }
    >
      <Table dataSource={ips} columns={columns} rowKey="id" loading={loading} pagination={false} scroll={{ x: 800 }} />

      <Modal
        title={editingId ? '編輯 IP' : '新增 IP'}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ port: 443, sortOrder: 0, isEnabled: true }}>
          <Form.Item name="ipAddress" label="IP 地址" rules={[{ required: true }, { validator: ipv4Validator }]}>
            <Input placeholder="104.16.xxx.xxx" />
          </Form.Item>
          <Form.Item name="port" label="端口" rules={[{ type: 'number', min: 1, max: 65535, message: '端口範圍為 1-65535' }]}>
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="region" label="地區" rules={[{ required: true }]}>
            <Input placeholder="HK / JP / US" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="isEnabled" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

/* ═══════════════════════════════════════ */
/*  Dashboard Tab                         */
/* ═══════════════════════════════════════ */

function DashboardTab() {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/vless/accounts/stats');
      const data = await res.json();
      if (data.code === 0) setStats(data.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  if (!stats) return null;

  const quotaPercent = stats.totalDailyQuota > 0
    ? Math.round((stats.usedDailyQuota / stats.totalDailyQuota) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 摘要卡片 */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <Text type="secondary">帳號總數</Text>
          <div style={{ fontSize: 28, fontWeight: 600 }}>{stats.totalAccounts}</div>
          <Text type="secondary">啟用: {stats.enabledAccounts}</Text>
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <Text type="secondary">今日配額使用</Text>
          <Progress
            percent={quotaPercent}
            status={quotaPercent > 80 ? 'exception' : 'normal'}
            format={() => `${stats.usedDailyQuota.toLocaleString()} / ${stats.totalDailyQuota.toLocaleString()}`}
          />
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <Text type="secondary">健康狀態</Text>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Tag color="green">{stats.accounts.filter(a => a.healthStatus === 'healthy').length} 正常</Tag>
            <Tag color="red">{stats.accounts.filter(a => a.healthStatus === 'down').length} 離線</Tag>
            <Tag>{stats.accounts.filter(a => a.healthStatus === 'unknown').length} 未知</Tag>
          </div>
        </Card>
      </div>

      {/* 帳號配額明細 */}
      <Card title="帳號配額明細" size="small" extra={
        <Button onClick={fetch_} icon={<SyncOutlined />} loading={loading}>重新整理</Button>
      }>
        <Table
          dataSource={stats.accounts}
          rowKey="id"
          pagination={false}
          columns={[
            { title: '帳號', dataIndex: 'accountName', width: 180 },
            { title: 'Worker', dataIndex: 'workerName', width: 200 },
            { title: '狀態', key: 'status', width: 100, render: (_: any, r: any) => <HealthTag status={r.healthStatus} /> },
            { title: '延遲', dataIndex: 'latencyMs', width: 100, render: (v: any) => v != null ? `${v}ms` : '-' },
            {
              title: '配額使用', key: 'usage', width: 220,
              render: (_: any, r: any) => (
                <Progress percent={r.usagePercent} size="small"
                  status={r.usagePercent > 80 ? 'exception' : 'normal'}
                  format={() => `${r.dailyRequestUsed.toLocaleString()}`}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
